import React, { useState } from 'react';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import MetricListContainer from 'src/components/BeaconV2Components/MetricList/MetricListContainer';
import { ProductColumnDefinition } from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import ProductGrid from 'src/components/BeaconV2Components/ProductGrid/ProductGrid';
import { useTaxonomySubtitleText } from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { useComparisonTimePeriodShortDisplayName, useMainTimePeriodShortDisplayName } from 'src/utils/Hooks';
import { shouldShowBeaconProTopics } from 'src/utils/app';

enum TrafficDisplayDropdownOption {
  Product = 'stacklineSku',
  Order = 'orderId',
  LineItem = 'lineItemId',
  Creative = 'creativeId'
}

const makeBaseHeadColumnDefinition = (headerName: string) => ({
  headerName,
  valueFormatter: (_, row: { name: string }) => <div>{row.name}</div>
});

const trafficDisplayDropdownOptionToDisplayName = {
  [TrafficDisplayDropdownOption.Product]: 'Product',
  [TrafficDisplayDropdownOption.Order]: 'Order',
  [TrafficDisplayDropdownOption.LineItem]: 'Line Item',
  [TrafficDisplayDropdownOption.Creative]: 'Creative'
};

const trafficDisplayDropdownOptionToColumnDefinition = {
  [TrafficDisplayDropdownOption.Product]: ProductColumnDefinition,
  [TrafficDisplayDropdownOption.Order]: makeBaseHeadColumnDefinition('Order'),
  [TrafficDisplayDropdownOption.LineItem]: makeBaseHeadColumnDefinition('Line Item'),
  [TrafficDisplayDropdownOption.Creative]: makeBaseHeadColumnDefinition('Creative')
};

/**
 * DPV grid by product, order, line item, or creative
 */
export default function TrafficDisplayGrid() {
  const [dropdownOption, setDropdownOption] = useState<TrafficDisplayDropdownOption>(
    TrafficDisplayDropdownOption.Product
  );

  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();
  const taxonomyText = useTaxonomySubtitleText();

  const getSubtitle = () => {
    return (
      <Text variant="body1" sx={{ marginTop: '16px' }}>
        {`The table displays the other traffic for DPV `}
        {taxonomyText}
        {` by ${trafficDisplayDropdownOptionToDisplayName[
          dropdownOption
        ].toLowerCase()} during the ${mainTimePeriodShortDisplayName} period compared to the ${comparisonTimePeriodShortDisplayName}.`}
        <SubtitleFilters />
      </Text>
    );
  };

  return (
    <MetricListContainer
      skeletonRows={20}
      showGrid={dropdownOption === TrafficDisplayDropdownOption.Product}
      renderGrid={(args) => <ProductGrid {...args} />}
      headColumnDefinition={trafficDisplayDropdownOptionToColumnDefinition[dropdownOption]}
      entityTableHeaderProps={{
        subTitle: shouldShowBeaconProTopics() ? () => getSubtitle() : undefined,
        prefixTitle: 'DPV by',
        title: '',
        groupByFields: Object.values(TrafficDisplayDropdownOption).map((option) => ({
          name: option,
          displayName: trafficDisplayDropdownOptionToDisplayName[option]
        })),
        handleGroupByChange: (event) => setDropdownOption(event.target.value as TrafficDisplayDropdownOption)
      }}
      fields={[
        {
          name: 'dpv'
        },
        {
          name: 'impressions'
        },
        {
          name: 'spend'
        },
        {
          name: 'sales'
        },
        {
          name: 'unitsSold'
        },
        {
          name: 'returnOnAdSpend'
        }
      ]}
      indexName="advertisingDisplay"
      sortFieldName="dpv"
      useTableDataProps={{
        groupByFieldName: dropdownOption,
        filterResponse: () => true
      }}
    />
  );
}
